import { createContext, useContext, useCallback, useRef } from "react";

/**
 * In-app notification data structure from server
 */
export interface AppNotification {
  id: string;
  type:
    | "new_lead"
    | "chat_message"
    | "tour_booked"
    | "escalation"
    | "info"
    | "warning"
    | "prospect_reassigned"
    // Feature B (D12): summary notification to the incoming holder when a role
    // reassignment moves items to them. Persisted + live-bumped like the
    // per-prospect reassign it sits beside.
    | "items_reassigned"
    | "prospect_created"
    | "prospect_updated"
    | "prospect_advanced"
    | "event_rsvped"
    | "attachment_uploaded"
    // SW-CRM-PROSPECT Follow → notifications: the follower-stream display type
    // (the specific event rides in the title/body + the prospect_follow.* pref).
    | "prospect_followed"
    // PLC W12 Inc-3b: "you're covering for <name>" (persisted, live-bumped).
    | "coverage_assigned";
  title: string;
  body: string;
  link?: string;
  linkText?: string;
  icon?: "person" | "chat" | "calendar" | "info" | "warning";
  timestamp: string;
  dismissable?: boolean;
  autoHideMs?: number;
}

type NotificationHandler = (notification: AppNotification) => void;

interface NotificationEventContextType {
  subscribe: (handler: NotificationHandler) => () => void;
  emit: (notification: AppNotification) => void;
  /** Suppress chat_message toast notifications (e.g., when on Inbox page) */
  setSuppressChatToasts: (suppress: boolean) => void;
  /** Check if chat toasts should be suppressed */
  shouldSuppressChatToasts: () => boolean;
  /** Check if a session has already shown a toast notification */
  hasNotifiedSession: (sessionId: string) => boolean;
  /** Mark a session as having shown a toast notification */
  markSessionNotified: (sessionId: string) => void;
}

export const NotificationEventContext = createContext<NotificationEventContextType | null>(null);

/**
 * Provider that allows components to subscribe to raw notification events.
 * This enables a single WebSocket connection to broadcast events to multiple subscribers.
 */
export function NotificationEventProvider({ children }: { children: React.ReactNode }) {
  const handlersRef = useRef<Set<NotificationHandler>>(new Set());
  const suppressChatToastsRef = useRef(false);
  // Track which sessions have already shown toast notifications (first message only)
  const notifiedSessionsRef = useRef<Set<string>>(new Set());

  const subscribe = useCallback((handler: NotificationHandler) => {
    handlersRef.current.add(handler);
    return () => {
      handlersRef.current.delete(handler);
    };
  }, []);

  const emit = useCallback((notification: AppNotification) => {
    handlersRef.current.forEach((handler) => handler(notification));
  }, []);

  const setSuppressChatToasts = useCallback((suppress: boolean) => {
    suppressChatToastsRef.current = suppress;
  }, []);

  const shouldSuppressChatToasts = useCallback(() => {
    return suppressChatToastsRef.current;
  }, []);

  const hasNotifiedSession = useCallback((sessionId: string) => {
    return notifiedSessionsRef.current.has(sessionId);
  }, []);

  const markSessionNotified = useCallback((sessionId: string) => {
    notifiedSessionsRef.current.add(sessionId);
  }, []);

  return (
    <NotificationEventContext.Provider
      value={{
        subscribe,
        emit,
        setSuppressChatToasts,
        shouldSuppressChatToasts,
        hasNotifiedSession,
        markSessionNotified,
      }}
    >
      {children}
    </NotificationEventContext.Provider>
  );
}

/**
 * Hook to subscribe to notification events from the single WebSocket connection.
 * Use this instead of creating a separate WebSocket connection.
 */
export function useNotificationEvents() {
  const context = useContext(NotificationEventContext);
  if (!context) {
    throw new Error("useNotificationEvents must be used within NotificationEventProvider");
  }
  return context;
}
